import type { MaterialSlot } from './model.js';
import type { CabinetParams } from './params.js';
import type { AssemblyInstance, AssemblyPanel } from './document.js';
import { validateParams, buildCabinet } from './cabinet.js';
import { evaluate, parseExpr, type Value } from './expr.js';

/**
 * Presets are immutable, versioned content. An instance stores presetId + versionId + its parameter values;
 * its resolved `params` are a snapshot so old projects never change when a preset gets a new version.
 */
export type PresetDim = 'length' | 'count';

export interface PresetParamDef {
  key: string;
  label: string;
  /** Target path inside CabinetParams (see PARAM_PATHS). */
  path: string;
  dim: PresetDim;
  default: number;
  min: number;
  max: number;
  step: number;
}

/** A dependent value computed from exposed parameters with the restricted expression language. */
export interface PresetDerived {
  path: string;
  expr: string;
}

export interface ParametricPresetContent {
  schemaVersion: 1;
  kind: 'parametric';
  base: CabinetParams;
  params: PresetParamDef[];
  derived: PresetDerived[];
  materials: Partial<Record<MaterialSlot, string>>;
  notes: string[];
}

export interface AssemblyPresetContent {
  schemaVersion: 1;
  kind: 'assembly';
  panels: AssemblyPanel[];
  materials: Partial<Record<MaterialSlot, string>>;
  notes: string[];
  /** Shelves, fronts, drawers, plinth / fillers, legs and slope of the saved assembly. */
  features?: Pick<AssemblyInstance, 'shelfSets' | 'fronts' | 'frontReveal' | 'drawerSets' | 'trims' | 'trimJoints' | 'legs' | 'slope'>;
}

export type PresetContent = ParametricPresetContent | AssemblyPresetContent;

/** Whitelisted numeric parameter paths a preset may expose or derive. Nothing else is writable. */
export const PARAM_PATHS: Record<string, { label: string; dim: PresetDim }> = {
  width: { label: 'Širina', dim: 'length' },
  height: { label: 'Visina korpusa', dim: 'length' },
  depth: { label: 'Dubina', dim: 'length' },
  plinthHeight: { label: 'Sokla', dim: 'length' },
  plinthSetback: { label: 'Uvlačenje sokle', dim: 'length' },
  legsHeight: { label: 'Nogice', dim: 'length' },
  sideThickness: { label: 'Debljina stranica', dim: 'length' },
  bottomThickness: { label: 'Debljina dna', dim: 'length' },
  topThickness: { label: 'Debljina vrha', dim: 'length' },
  backThickness: { label: 'Debljina leđa', dim: 'length' },
  shelfThickness: { label: 'Debljina polica', dim: 'length' },
  dividerThickness: { label: 'Debljina pregrada', dim: 'length' },
  frontThickness: { label: 'Debljina fronti', dim: 'length' },
  dividers: { label: 'Broj pregrada', dim: 'count' },
  'shelves.count': { label: 'Broj polica', dim: 'count' },
  'shelves.frontSetback': { label: 'Uvučenje police', dim: 'length' },
  'shelves.sideClearance': { label: 'Bočni zazor police', dim: 'length' },
  'back.grooveInset': { label: 'Uvlačenje žlijeba leđa', dim: 'length' },
  'back.grooveDepth': { label: 'Dubina žlijeba', dim: 'length' },
  'fronts.gapOuter': { label: 'Vanjski zazor fronti', dim: 'length' },
  'fronts.gapBetween': { label: 'Zazor između fronti', dim: 'length' },
  'drawerSystem.sideClearance': { label: 'Bočni razmak vodilice', dim: 'length' },
  'drawerSystem.nominalLength': { label: 'Dužina vodilice', dim: 'length' },
  ...Object.fromEntries(Array.from({ length: 12 }, (_, i) => [`fronts.rows.${i}.height`, { label: `Visina fronte ${i + 1}`, dim: 'length' as const }])),
};

const FORBIDDEN = new Set(['__proto__', 'prototype', 'constructor']);

export function getPath(obj: unknown, path: string): unknown {
  let cur: unknown = obj;
  for (const seg of path.split('.')) {
    if (FORBIDDEN.has(seg) || cur === null || typeof cur !== 'object') return undefined;
    cur = (cur as Record<string, unknown>)[seg];
  }
  return cur;
}

/** Sets a whitelisted path. Throws for anything outside PARAM_PATHS or missing intermediate objects. */
export function setParamPath(params: CabinetParams, path: string, value: number): void {
  if (!(path in PARAM_PATHS)) throw new Error(`Putanja ${path} nije dozvoljena.`);
  const segs = path.split('.');
  let cur: Record<string, unknown> = params as unknown as Record<string, unknown>;
  for (const seg of segs.slice(0, -1)) {
    const next = cur[seg];
    if (next === null || typeof next !== 'object') throw new Error(`Putanja ${path} ne postoji u ovoj konfiguraciji.`);
    cur = next as Record<string, unknown>;
  }
  const last = segs.at(-1)!;
  if (!(last in cur)) throw new Error(`Putanja ${path} ne postoji u ovoj konfiguraciji.`);
  cur[last] = value;
}

export interface ResolveResult {
  params: CabinetParams | null;
  errors: string[];
}

function checkValue(def: PresetParamDef, v: number): string | null {
  if (!Number.isFinite(v)) return `${def.label}: vrijednost nije broj.`;
  if (!Number.isInteger(v)) return `${def.label}: vrijednost mora biti cijeli broj${def.dim === 'length' ? ' µm' : ''}.`;
  if (v < def.min || v > def.max) return `${def.label}: dozvoljeno ${fmt(def, def.min)}–${fmt(def, def.max)}.`;
  return null;
}

const fmt = (def: PresetParamDef, v: number) => (def.dim === 'length' ? `${v / 1000} mm` : String(v));

/** Validates the preset definition itself (not a particular configuration). */
export function validatePresetContent(c: PresetContent): string[] {
  const errors: string[] = [];
  if (c.kind === 'assembly') {
    if (c.panels.length === 0) errors.push('Sklop nema nijednu ploču.');
    return errors;
  }
  const keys = new Set<string>();
  for (const p of c.params) {
    if (!/^[a-zA-Z][a-zA-Z0-9_]{0,40}$/.test(p.key)) errors.push(`Neispravan ključ parametra "${p.key}".`);
    if (keys.has(p.key)) errors.push(`Dupli parametar "${p.key}".`);
    keys.add(p.key);
    const meta = PARAM_PATHS[p.path];
    if (!meta) errors.push(`Parametar ${p.key}: putanja ${p.path} nije dozvoljena.`);
    else if (meta.dim !== p.dim) errors.push(`Parametar ${p.key}: pogrešna jedinica.`);
    if (!(p.min <= p.default && p.default <= p.max)) errors.push(`Parametar ${p.key}: mora važiti min ≤ default ≤ max.`);
    if (!(p.step > 0)) errors.push(`Parametar ${p.key}: korak mora biti pozitivan.`);
    if (getPath(c.base, p.path) === undefined) errors.push(`Parametar ${p.key}: putanja ${p.path} ne postoji u osnovnoj konfiguraciji.`);
  }
  const exposedPaths = new Set(c.params.map((p) => p.path));
  for (const d of c.derived) {
    if (!(d.path in PARAM_PATHS)) errors.push(`Izvedena vrijednost: putanja ${d.path} nije dozvoljena.`);
    if (exposedPaths.has(d.path)) errors.push(`Izvedena vrijednost ${d.path} je već izložen parametar.`);
    try {
      parseExpr(d.expr);
    } catch (e) {
      errors.push(`Izraz za ${d.path}: ${(e as Error).message}`);
    }
  }
  return errors;
}

/** Resolves a parametric preset with instance values into full CabinetParams. Never throws. */
export function resolvePreset(c: ParametricPresetContent, values: Record<string, number>): ResolveResult {
  const errors = validatePresetContent(c);
  if (errors.length) return { params: null, errors };
  const params = structuredClone(c.base);
  const scope = new Map<string, Value>();
  for (const def of c.params) {
    const v = values[def.key] ?? def.default;
    const e = checkValue(def, v);
    if (e) {
      errors.push(e);
      continue;
    }
    scope.set(def.key, { value: v, dim: def.dim === 'length' ? 'length' : 'scalar' });
    try {
      setParamPath(params, def.path, v);
    } catch (err) {
      errors.push((err as Error).message);
    }
  }
  for (const d of c.derived) {
    try {
      const r = evaluate(parseExpr(d.expr), (n) => scope.get(n));
      const meta = PARAM_PATHS[d.path]!;
      if ((meta.dim === 'length') !== (r.dim === 'length')) throw new Error('pogrešna jedinica rezultata');
      setParamPath(params, d.path, Math.round(r.value));
    } catch (err) {
      errors.push(`Izraz za ${d.path}: ${(err as Error).message}`);
    }
  }
  if (errors.length) return { params: null, errors };
  for (const i of validateParams('preset', params)) if (i.severity === 'error') errors.push(i.message);
  return errors.length ? { params: null, errors } : { params, errors: [] };
}

export interface RangeCase {
  label: string;
  values: Record<string, number>;
  errors: string[];
}

/** Tests a parametric preset at all-min, default, all-max and each parameter at its own min/max. */
export function testPresetRange(c: ParametricPresetContent): RangeCase[] {
  const defaults = Object.fromEntries(c.params.map((p) => [p.key, p.default]));
  const cases: { label: string; values: Record<string, number> }[] = [
    { label: 'Sve podrazumijevano', values: defaults },
    { label: 'Sve minimalno', values: Object.fromEntries(c.params.map((p) => [p.key, p.min])) },
    { label: 'Sve maksimalno', values: Object.fromEntries(c.params.map((p) => [p.key, p.max])) },
  ];
  for (const p of c.params) {
    if (p.min !== p.default) cases.push({ label: `${p.label} = min`, values: { ...defaults, [p.key]: p.min } });
    if (p.max !== p.default) cases.push({ label: `${p.label} = max`, values: { ...defaults, [p.key]: p.max } });
  }
  return cases.map((tc) => {
    const r = resolvePreset(c, tc.values);
    const errors = [...r.errors];
    if (r.params) {
      for (const i of buildCabinet('test', r.params).issues) if (i.severity === 'error') errors.push(i.message);
    }
    return { ...tc, errors };
  });
}

/** Flattens CabinetParams into leaf paths (arrays are leaves) for override detection and diffs. */
export function flattenParams(obj: unknown, prefix = '', out: Record<string, string> = {}): Record<string, string> {
  if (obj !== null && typeof obj === 'object' && !Array.isArray(obj)) {
    for (const [k, v] of Object.entries(obj)) {
      if (FORBIDDEN.has(k)) continue;
      flattenParams(v, prefix ? `${prefix}.${k}` : k, out);
    }
  } else {
    out[prefix] = JSON.stringify(obj);
  }
  return out;
}

function setLeaf(obj: Record<string, unknown>, path: string, json: string) {
  const segs = path.split('.');
  if (segs.some((s) => FORBIDDEN.has(s))) return;
  let cur = obj;
  for (const s of segs.slice(0, -1)) {
    if (cur[s] === null || typeof cur[s] !== 'object') return;
    cur = cur[s] as Record<string, unknown>;
  }
  cur[segs.at(-1)!] = JSON.parse(json);
}

export interface ParamDiffEntry {
  path: string;
  label: string;
  before: string;
  after: string;
}

export function diffParams(a: CabinetParams, b: CabinetParams): ParamDiffEntry[] {
  const fa = flattenParams(a);
  const fb = flattenParams(b);
  const keys = [...new Set([...Object.keys(fa), ...Object.keys(fb)])].sort();
  return keys
    .filter((k) => fa[k] !== fb[k])
    .map((k) => ({ path: k, label: PARAM_PATHS[k]?.label ?? k, before: fa[k] ?? '—', after: fb[k] ?? '—' }));
}

/**
 * Re-resolves an instance after its preset values and/or version change.
 * Local overrides = leaves where `current` differs from what (oldContent, oldValues) would produce;
 * they are re-applied on top of (newContent, newValues). Values of removed parameters are dropped and reported.
 */
export function reresolvePreset(
  oldContent: ParametricPresetContent,
  oldValues: Record<string, number>,
  newContent: ParametricPresetContent,
  newValues: Record<string, number>,
  current: CabinetParams,
): { params: CabinetParams | null; values: Record<string, number>; overrides: string[]; dropped: string[]; diff: ParamDiffEntry[]; errors: string[] } {
  const oldResolved = resolvePreset(oldContent, oldValues);
  const newKeys = new Set(newContent.params.map((p) => p.key));
  const keptValues = Object.fromEntries(Object.entries(newValues).filter(([k]) => newKeys.has(k)));
  const dropped = Object.keys(newValues).filter((k) => !newKeys.has(k));
  const next = resolvePreset(newContent, keptValues);
  if (!next.params) return { params: null, values: keptValues, overrides: [], dropped, diff: [], errors: next.errors };
  const overrides: string[] = [];
  if (oldResolved.params) {
    const fo = flattenParams(oldResolved.params);
    const fc = flattenParams(current);
    for (const [path, v] of Object.entries(fc)) {
      if (fo[path] !== v) {
        overrides.push(path);
        setLeaf(next.params as unknown as Record<string, unknown>, path, v);
      }
    }
  }
  const errors = validateParams('preset', next.params).filter((i) => i.severity === 'error').map((i) => i.message);
  return { params: errors.length ? null : next.params, values: keptValues, overrides, dropped, diff: diffParams(current, next.params), errors };
}

/** Update of an instance to a new preset version, keeping its values and local overrides. */
export function planPresetUpdate(
  oldContent: ParametricPresetContent,
  newContent: ParametricPresetContent,
  values: Record<string, number>,
  current: CabinetParams,
) {
  return reresolvePreset(oldContent, values, newContent, values, current);
}
